import React, { useState, useEffect } from 'react';
import { 
    Plus, Edit3, Trash2, CheckCircle, AlertTriangle, Layers, 
    RotateCcw, Sparkles, Settings 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const CreationExtras = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [extras, setExtras] = useState([]);
  const [composantsDisponibles, setComposantsDisponibles] = useState([]);
  const [activeTab, setActiveTab] = useState('actives'); // 'actives' ou 'supprimees'
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Formulaire
  const [formData, setFormData] = useState({ 
    code: '', 
    composant_id: '' 
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [modalConfig, setModalConfig] = useState({ show: false, action: null, message: '', id: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'supprimees' ? '/hebergement/extras?status=inactive' : '/hebergement/extras';
      
      const [resExtras, resComposants] = await Promise.all([
        API.get(endpoint).catch(() => ({ data: [] })),
        API.get('/hebergement/composants').catch(() => ({ data: [] }))
      ]);
      
      const rawExtras = resExtras.data;
      setExtras(Array.isArray(rawExtras) ? rawExtras : (rawExtras?.data || rawExtras?.items || []));

      const rawComp = resComposants.data;
      const listComp = Array.isArray(rawComp) ? rawComp : (rawComp?.composants || rawComp?.data || rawComp?.items || []);
      
      setComposantsDisponibles(listComp.map(c => ({
        id: c.id,
        libelle: c.libelle || c.nom || 'Sans nom',
        prix_unitaire: Number(c.prix_defaut || c.prix_unitaire || 0),
        soumis_tva: c.soumis_tva === 1 || c.soumis_tva === true,
        taux_tva: Number(c.taux_tva || 18.0)
      })));

    } catch (err) {
      console.error("Erreur chargement services extras:", err);
      showToast("Erreur de chargement des services extras", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim() || !formData.composant_id) {
      showToast("Le code et le composant d'hébergement sont obligatoires", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = { 
        code: formData.code.toUpperCase().trim(), 
        composant_id: formData.composant_id
      };

      if (editingId) {
        await API.put(`/hebergement/extras/${editingId}`, payload);
        showToast("Service extra mis à jour avec succès");
      } else {
        await API.post('/hebergement/extras', payload);
        showToast("Service extra créé avec succès");
      }

      closeForm();
      fetchData();
    } catch (err) {
      console.error("Erreur enregistrement service extra :", err);
      const errorMsg = err.response?.data?.error || "Erreur lors de l'enregistrement";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setFormData({
      code: item.code || '',
      composant_id: item.composant_id || ''
    });
    setIsAdding(true);
  };

  const handleDeleteClick = (id) => {
    setModalConfig({
      show: true,
      action: () => executeDelete(id),
      message: "Voulez-vous désactiver ce service extra ?",
      id
    });
  };

  const executeDelete = async (id) => {
    setLoading(true);
    try {
      await API.delete(`/hebergement/extras/${id}`);
      showToast("Service extra désactivé avec succès");
      setModalConfig({ show: false, action: null, message: '', id: null });
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Impossible de désactiver ce service.";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = async (id) => {
    setLoading(true);
    try {
      await API.put(`/hebergement/extras/${id}/restore`);
      showToast("Service extra restauré avec succès");
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de la restauration.", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ code: '', composant_id: '' });
  };

  // Trouver les détails du composant sélectionné pour aperçu dynamique
  const selectedComposantDetails = composantsDisponibles.find(c => c.id === formData.composant_id);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        {/* HEADER CUSTOMISÉ VIOLET/INDIGO */}
        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px', color: '#1e1b4b' }}>
              <Sparkles size={22} color="#7c3aed" /> SERVICES EXTRAS & PRESTATIONS
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Gestion des prestations hors hébergement (Piscine, Spa, Blanchisserie, etc.)
            </div>
          </div>
          <div>
            {!isAdding && !editingId && activeTab === 'actives' && (
              <button onClick={() => setIsAdding(true)} style={btnPrimaryStyle}>
                <Plus size={18} /> Nouveau Service Extra
              </button>
            )}
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>

          {/* BARRE D'ONGLETS */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <div style={{ display: 'flex', background: '#e2e8f0', padding: '4px', borderRadius: '10px', gap: '4px' }}>
              <button 
                onClick={() => { setActiveTab('actives'); closeForm(); }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: activeTab === 'actives' ? 'white' : 'transparent', fontWeight: '700', cursor: 'pointer', color: activeTab === 'actives' ? '#7c3aed' : '#64748b', boxShadow: activeTab === 'actives' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
              >
                Services Actifs
              </button>
              <button 
                onClick={() => { setActiveTab('supprimees'); closeForm(); }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: activeTab === 'supprimees' ? 'white' : 'transparent', fontWeight: '700', cursor: 'pointer', color: activeTab === 'supprimees' ? '#ef4444' : '#64748b', boxShadow: activeTab === 'supprimees' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
              >
                Services Désactivés
              </button>
            </div>
          </div>

          {/* FORMULAIRE DE CRÉATION / MODIFICATION */}
          {(isAdding || editingId) && activeTab === 'actives' && (
            <div style={{ ...contentCardStyle, padding: '25px', marginBottom: '25px', border: '2px solid #7c3aed' }}>
              <h3 style={{ fontSize: '15px', marginBottom: '20px', fontWeight: '900', color: '#1e1b4b', display: 'flex', alignItems: 'gap: 8px' }}>
                <Settings size={18} color="#7c3aed"/> {editingId ? 'Modifier le service extra' : 'Rattacher un nouveau service extra'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'flex-end' }}>
                  
                  <div style={{ flex: '1 1 200px' }}>
                    <label style={labelStyle}>CODE INTERNE (ex: SPA, PISCINE)</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Ex: SPA01"
                      value={formData.code} 
                      onChange={(e) => setFormData({...formData, code: e.target.value})} 
                      required 
                    />
                  </div>

                  <div style={{ flex: '2 1 300px' }}>
                    <label style={labelStyle}>COMPOSANT DU CATALOGUE (Liaison Tarifaire)</label>
                    <select 
                      style={inputStyle} 
                      value={formData.composant_id} 
                      onChange={(e) => setFormData({...formData, composant_id: e.target.value})} 
                      required
                    >
                      <option value="">-- Sélectionner dans le catalogue --</option>
                      {composantsDisponibles.map((comp) => (
                        <option key={comp.id} value={comp.id}>
                          {comp.libelle} — {Math.round(comp.prix_unitaire).toLocaleString()} F
                        </option>
                      ))}
                    </select>
                  </div>

                </div>

                {/* APERÇU DYNAMIQUE DU TARIF LIÉ */}
                {selectedComposantDetails && (
                  <div style={{ marginBottom: '20px', background: '#f5f3ff', border: '1px dashed #7c3aed', padding: '12px 16px', borderRadius: '8px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#5b21b6', fontWeight: '800' }}>
                      💡 Aperçu catalogue :
                    </div>
                    <div style={{ fontSize: '12px', color: '#334155' }}>
                      Prix unitaire : <strong style={{ color: '#7c3aed' }}>{Math.round(selectedComposantDetails.prix_unitaire).toLocaleString()} F</strong>
                    </div>
                    <div style={{ fontSize: '12px', color: '#334155' }}>
                      TVA : <strong style={{ color: '#059669' }}>{selectedComposantDetails.soumis_tva ? `${selectedComposantDetails.taux_tva}%` : 'Exonéré'}</strong>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={loading} style={btnSubmitStyle}>{loading ? '...' : 'Valider'}</button>
                  <button type="button" onClick={closeForm} style={btnCancelStyle}>Annuler</button>
                </div>
              </form>
            </div>
          )}

          {/* TABLEAU DES SERVICES EXTRAS */}
          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Désignation (Catalogue)</th>
                  <th style={thStyle}>Prix Unitaire</th>
                  <th style={thStyle}>Régime TVA</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {extras.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      {activeTab === 'actives' ? "Aucun service extra enregistré." : "Aucun service désactivé."}
                    </td>
                  </tr>
                ) : (
                  extras.map((item, idx) => {
                    const uniqueKey = item.id ? `extra-${item.id}-${idx}` : `extra-idx-${idx}`;
                    return (
                      <tr key={uniqueKey} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...tdStyle, fontWeight: '800' }}>
                          <code style={codeStyle}>{item.code}</code>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '700', color: '#1e293b' }}>
                          {item.libelle || '---'}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '800', color: '#7c3aed' }}>
                          {Number(item.prix_unitaire || 0).toLocaleString()} F
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: item.soumis_tva ? '#059669' : '#64748b' }}>
                            {item.soumis_tva ? `Soumis (${item.taux_tva}%)` : 'Non soumis'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {activeTab === 'actives' ? (
                              <>
                                <button onClick={() => handleEditClick(item)} style={actionBtnStyle('#7c3aed')} title="Modifier">
                                  <Edit3 size={14}/> Modifier
                                </button>
                                <button onClick={() => handleDeleteClick(item.id)} style={actionBtnStyle('#ef4444')} title="Désactiver">
                                  <Trash2 size={14}/> Désactiver
                                </button>
                              </>
                            ) : (
                              <button onClick={() => handleRestoreClick(item.id)} style={actionBtnStyle('#10b981')}>
                                <RotateCcw size={14}/> Restaurer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

        </div>
      </main>

      {modalConfig.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '50%' }}><AlertTriangle size={24} /></div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Confirmation</h2>
            </div>
            <p style={{ color: '#475569', marginBottom: '30px' }}>{modalConfig.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfig({ show: false, action: null, message: '', id: null })} style={btnSecondaryStyle}>Annuler</button>
              <button onClick={modalConfig.action} style={btnDangerStyle}>{loading ? '...' : 'Confirmer'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES (Design Violet/Indigo épuré) ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#7c3aed', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700', fontSize: '13px' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569', textTransform: 'uppercase' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '14px 20px', fontSize: '13px', color: '#334155' };
const codeStyle = { fontSize: '12px', background: '#f5f3ff', padding: '4px 8px', borderRadius: '4px', color: '#7c3aed', fontWeight: '900', border: '1px solid #ede9fe' };
const btnSubmitStyle = { padding: '10px 25px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const btnCancelStyle = { padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const actionBtnStyle = (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const btnDangerStyle = { background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };

export default CreationExtras;